// Therapist chip rail. In the faceted flow this is a GLOBAL filter (one pick
// applies to every selected treatment), not a per-card control. Selected state
// uses the widget's theme-safe pattern (accent border + accent-soft fill + a
// check) — never white-on-accent, which vanishes on light-accent tenants.

import type { WidgetChideskStaff } from "../../types";

export function TherapistPicker({
  staff,
  value,
  onChange,
  /** When provided, therapists NOT in this set are shown disabled (they have no
   *  slot in the current facet). "Any available" is always enabled. */
  enabledIds,
}: {
  staff: WidgetChideskStaff[];
  value: string | null;
  onChange: (staffId: string | null) => void;
  enabledIds?: Set<string> | null;
}) {
  return (
    <div className="vt-chidesk-therapists">
      <span className="vt-chidesk-therapists-label">Therapist</span>
      <div className="vt-therapist-chips">
        <button
          type="button"
          className={`vt-therapist-chip${value === null ? " vt-therapist-chip-active" : ""}`}
          onClick={() => onChange(null)}
          aria-pressed={value === null}
        >
          <span className="vt-therapist-avatar vt-therapist-avatar-any" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
            </svg>
          </span>
          <span className="vt-therapist-name">Any available</span>
          {value === null && (
            <span className="vt-therapist-check" aria-hidden="true">
              ✓
            </span>
          )}
        </button>
        {staff.map((s) => {
          const name = s.name?.trim() || "Therapist";
          const active = value === s.id;
          const disabled = enabledIds != null && !enabledIds.has(s.id) && !active;
          const initials = name
            .split(/\s+/)
            .map((w) => w[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase();
          return (
            <button
              key={s.id}
              type="button"
              className={`vt-therapist-chip${active ? " vt-therapist-chip-active" : ""}${
                disabled ? " vt-chip-busy" : ""
              }`}
              onClick={() => onChange(s.id)}
              aria-pressed={active}
              disabled={disabled}
            >
              <span className="vt-therapist-avatar" aria-hidden="true">
                {s.avatar_url ? (
                  <img src={s.avatar_url} alt="" loading="lazy" decoding="async" />
                ) : (
                  initials
                )}
              </span>
              <span className="vt-therapist-name">{name}</span>
              {active && (
                <span className="vt-therapist-check" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
